import React, { useCallback, useEffect, useMemo } from 'react';
import Link from 'next/link';
import {
  Configure,
  Hits,
  InstantSearch,
  useInstantSearch
} from 'react-instantsearch';
import { AlgoliaSearchHit } from '../../AlgoliaSearchHit/AlgoliaSearchHit';
import {
  ALGOLIA_INDEX_EVENTS_SEARCH,
  labelsAnalyticsTags
} from 'constants/algolia';
import searchClient, { createAnalyticsTags } from 'utils/algolia';
import { createLinkToRedirect } from 'utils/createLinkToRedirect';
import { ANYWHERE_LOCATION_JSON } from 'constants/index';
import { IconClose } from '@sympla/sympla-components';
import {
  analytics,
  analyticsEvents,
  analyticsLabels,
  transportMethods
} from 'lib/analytics';

import * as styles from './AlgoliaPopularEvents.css';

function PopularEventSkeleton() {
  return (
    <div className={styles.skeletonContainer}>
      <div className={`${styles.skeletonImage} ${styles.skeletonPulse}`}></div>
      <div className={styles.skeletonContent}>
        <div className={`${styles.skeletonLine} ${styles.skeletonPulse}`}></div>
        <div className={`${styles.skeletonLine} ${styles.skeletonPulse}`}></div>
        <div
          className={`${styles.skeletonShortLine} ${styles.skeletonPulse}`}
        ></div>
      </div>
    </div>
  );
}

function EventsSection({
  query,
  hasMossColor,
  isNavbarCollapsed,
  isMobile,
  hitsLimit,
  onClose,
  onStateChange
}: {
  query: string;
  hasMossColor: boolean;
  isNavbarCollapsed: boolean;
  isMobile: boolean;
  hitsLimit: number;
  onClose: () => void;
  onStateChange: (hasHits: boolean, isLoading: boolean) => void;
}) {
  const { results, status } = useInstantSearch();

  const isAlgoliaLoading =
    status === 'loading' || status === 'stalled' || !results;
  const hasHits = results?.nbHits > 0;

  useEffect(() => {
    onStateChange(hasHits, isAlgoliaLoading);
  }, [hasHits, isAlgoliaLoading, onStateChange]);

  const handleTrackAnalyticsClick = useCallback(
    ({ eventName, eventId, eventCategory, position }: any) => {
      analytics.sendEvent(analyticsEvents.search_recommended_event_clicked, {
        event_id: eventId,
        event_name: eventName,
        event_rank: position,
        search_recommendation_source_term: query,
        event_category: eventCategory
      });
    },
    [query]
  );

  const seeAllButton = () => {
    analytics.sendEvent(analyticsEvents.rail_view_more_clicked, {
      item_rail_title: analyticsLabels.recommendedEvents,
      transport: transportMethods.sendBeacon
    });
    onClose();
  };

  if (!isAlgoliaLoading && !hasHits) return null;

  const safeHitsLimit = hitsLimit || 6;
  const skeletonItems = Array.from({ length: safeHitsLimit });

  return (
    <div className={styles.eventsSectionContainer}>
      <div className={styles.titleContainer}>
        <h3 className={styles.sectionTitle}>Eventos</h3>
        <div className={styles.eventsHeader}>
          <Link
            href={createLinkToRedirect({
              param: `?s=${query}`,
              routeType: 'search-bar-default-filter',
              userLocation: ANYWHERE_LOCATION_JSON
            })}
            className={`${styles.eventsLink} ${
              hasMossColor && styles.buttonLinkMoss
            } `}
            onClick={seeAllButton}
          >
            Ver tudo
          </Link>
          {isNavbarCollapsed && !isMobile && (
            <IconClose
              width={20}
              height={20}
              onClick={() => onClose()}
              className={`${styles.closeInput} ${
                hasMossColor ? styles.closeInputMoss : ''
              }`}
            />
          )}
        </div>
      </div>
      <div className={styles.eventsContainer}>
        {isAlgoliaLoading && (
          <div className={styles.skeletonListWrapper}>
            {skeletonItems.map((_, index) => (
              <div key={index} className={styles.skeletonItemWrapper}>
                <PopularEventSkeleton />
              </div>
            ))}
          </div>
        )}

        <div className={isAlgoliaLoading ? styles.hitsHidden : ''}>
          <Hits
            hitComponent={(props) => (
              <AlgoliaSearchHit
                hit={props.hit as IHit}
                handleTrackAnalyticsClick={handleTrackAnalyticsClick}
              />
            )}
            classNames={{
              list: styles.HitsList
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default function AlgoliaPopularEvents({
  userTokenAlgolia,
  hitsLimit,
  query,
  isNavbarCollapsed,
  hasMossColor,
  isMobile,
  onClose,
  onStateChange,
  labelTagsAnalytics = []
}: {
  userTokenAlgolia: string;
  hitsLimit: number;
  query: string;
  isNavbarCollapsed: boolean;
  hasMossColor: boolean;
  isMobile: boolean;
  onClose: () => void;
  onStateChange: (hasHits: boolean, isLoading: boolean) => void;
  labelTagsAnalytics?: string[];
}) {
  const analyticsTags = useMemo(() => {
    return createAnalyticsTags({
      groupTag: labelsAnalyticsTags.recommendationSearch,
      componentTag: labelsAnalyticsTags.trendingEventsSearch,
      otherTags: labelTagsAnalytics
    });
  }, [labelTagsAnalytics]);

  return (
    <div data-testid="search-algolia-dropdown-events">
      <InstantSearch
        searchClient={searchClient}
        indexName={ALGOLIA_INDEX_EVENTS_SEARCH}
        insights={true}
      >
        <Configure
          hitsPerPage={hitsLimit}
          query={query}
          userToken={userTokenAlgolia}
          analytics={true}
          clickAnalytics={true}
          analyticsTags={analyticsTags}
        />
        <EventsSection
          query={query}
          isNavbarCollapsed={isNavbarCollapsed}
          hasMossColor={hasMossColor}
          isMobile={isMobile}
          hitsLimit={hitsLimit}
          onClose={onClose}
          onStateChange={onStateChange}
        />
      </InstantSearch>
    </div>
  );
}
